<template>
  <div style="max-width: 1200px">
    <ProcessBarModal
        :title="$t('Uploading file on the server')"
        :show-modal="showUploadModal"
        :step="uploadStep"
        :file-name="uploadingFileName"
        :progress="uploadProgress"
        :error-text="uploadError"
        :steps-text="{upload: 'Uploading file on the server', inject: 'Injecting members', success: 'Rates injected', error: uploadError}"
        @close="() => showUploadModal = false"
    />
    <CRow class="w-100 gap-8 align-items-center">
      <ul class="pages-nav">
        <li
            v-for="page in pages"
            :key="page.page"
            @click="switchPage(page.page)"
            :class="{'is-current-page': isCurrentPage(page.page)}"
        >
          {{ $t(`${page.name}`) }}
        </li>
      </ul>
      <CRow class="flex justify-content-start align-items-center">
      <CButton
                v-if="hasAccessComplete"
                class="mz-button justify-content-center align-items-end v2-mz-btn mr-2"
                color="primary"
                @click="downloadFile('Cover_Year_CCR_Threshold.xlsx')"
                :disabled="!hasAccessAdmin()"
            >
              <span>
                {{ $t("Download") }}
                <em class="ml-2 mz-icon icon-download color-inherit"/>
              </span>
            </CButton>
              <file-dragdrop
                      v-if="can_upload_coveryear_file"
                      :disabled="!can_upload_coveryear_file"
                      :title="$t('Upload cover year file')"
                      @upload="handleUpload"

                  >
                    <template #body-content>
                      {{
                        $t("Only xlsx files allowed")
                      }}
                    </template>
                    <template #upload-button>
                      <CButton
                          :disabled="!can_upload_coveryear_file || updating_coveryear"
                          color="primary"
                          class="mz-button v2-mz-btn mr-2"
                      >
                        <DotsLoader v-if="uploadingFiles"/>
                        <span v-else>
                    {{ $t("Upload") }}
                  </span>
                      </CButton>
                    </template>
                  </file-dragdrop>
                </CRow>
    </CRow>
    <CRow class="w-100">
      <CCard v-if="current_page === 'coveryear_details'" class="w-75">
        <CCardBody>
          <div class="d-flex justify-content_between w-100 mb-1">
            <div class="flex-grow-1"></div>

                  <div class="d-flex justify-content-end">
            <CButton
                  v-if="hasAccessComplete"
                  class="btn mz-button v2-mz-btn mr-2 btn-primar"
                  color="primary"
                  @click="updateCoverYearCCRThreshold"
                  :disabled="!hasAccessAdmin() && updating_coveryear && (this.selected_coveryear_id=null)"
              >
                <DotsLoader v-if="updating_coveryear"/>
                <span v-else>
                  {{ $t("Save") }}
                  <em class="ml-2 mz-icon icon-save color-inherit"/>
                </span>
              </CButton>
            </div>
          </div>
          <CForm class="d-flex flex-column gap-8">
            <div>
              <label>
                <div class="title">{{ $t("Year identification") }}</div>
              </label>
              <div>
                <MzSelect
                  class="mb-3"
                  :options="coveryear_ids"
                  :value.sync="selected_coveryear_id"
                  @input="handleCoverYearIdChange"
                  @update:value="fetchBranches"
                />
              </div>
            </div>
            <hr/>
            <div>
              <label>
                <div class="title">{{ $t("CCR Threshold Rates") }}</div>
              </label>
              <div class="gareat-rates">
                <CRow
                    class="w-100"
                    v-for="(rates_row, index) in rates_list_in_rows"
                    :key="`${rates_row.length}_${index}`"
                >
                  <CCol
                      v-for="rate in rates_row"
                      col="6"
                      :key="rate.label"
                  >
                    <label>{{ $t(rate.label) }} (%):</label>
                    <div class="d-flex">
                      <CInput
                          :key="`${rate.label}_${rate.value}`"
                          :disabled="isSuperUser()"
                          class="w-90"
                          addInputClasses="mz-input text-right w-350"
                          type="number"
                          :value="rate.value > 0 ? getNumericalValue(rate.value, 2) : rate.value"
                          :min=0
                          :max=100
                          @change="v => handleRateInput(rate.label, v)"
                      />
                      <span class="ml-1 mt-1">%</span>
                    </div>
                  </CCol>
                </CRow>
              </div>
            </div>
          </CForm>
          <span v-if="selected_coveryear_id!=null">
            <CRow class="w-100 mt-2 p-3">
              <span v-if="coveryear_data.coveryear_CCR_threshold[selected_coveryear_id]">
              {{ $t("The cover year configuration was") }}&nbsp;
              <span v-if="coveryear_data.coveryear_CCR_threshold[selected_coveryear_id][0].user_modification">
                {{ $t("updated by") }}
                {{ coveryear_data.coveryear_CCR_threshold[selected_coveryear_id][0].user_modification }}
                {{ $t("on") }}
                {{ formatDate(coveryear_data.coveryear_CCR_threshold[selected_coveryear_id][0].date_modification) }}
              </span>
              <span v-else>
                {{ $t("created by") }}
                {{ coveryear_data.coveryear_CCR_threshold[selected_coveryear_id][0].user_creation }}
                {{ $t("on") }}
                {{ formatDate(coveryear_data.coveryear_CCR_threshold[selected_coveryear_id][0].date_creation) }}
              </span>
            </span>
            </CRow>
          </span>
        </CCardBody>
      </CCard>
    </CRow>
  </div>
</template>

<script>
import {COVERYEARS_ENDPOINTS, CoverYearServices} from "@/services/coveryears";
import {BranchesServices} from "@/services/branches";
import moment from "moment/moment";
import {ActiveUser} from "@/services/user";
import {userRoles} from "../../constants/userRoles";
import {getNumericalValue} from "@/utils/helpers";
import ProcessBarModal from "@/components/ProcessBarModal.vue";
import {downloadFile} from "@/utils/request";

export default {
  name: 'CoverYearCCRThreshold',
  components: {ProcessBarModal},
  data() {
    return {
      COVERYEARS_ENDPOINTS,
      getNumericalValue,
      showModal: false,
      updating_coveryear: false,
      current_page: 'coveryear_details',
      coveryear_id: this.$route.params.coveryearId,
      selected_coveryear_id: null,
      coveryear_ids: [],
      coveryear_data: {},
      rates: [],
      pages: [
        {
          page: 'coveryear_details',
          name: 'CCR Threshold Rates',
          only_admin: false
        },
      ],
      row_size: 2,
      uploadingFiles: false,
      uploadProgress: 0,
      uploadStep: 'upload',
      uploadingFileName: '',
      showUploadModal: false,
      uploadError: ''
    }
  },
  computed: {
    can_upload_coveryear_file() {
      return !this.coveryear_data?.coveryear?.date_upload
    },
    rates_list_in_rows() {
      const coveryear_list_in_rows = []
      for (let i = 0; i < this.rates.length; i += this.row_size) {
        const chunk = this.rates.slice(i, i + this.row_size);
        coveryear_list_in_rows.push(chunk)
      }
      return coveryear_list_in_rows
    },
    hasAccessComplete() {
      return !ActiveUser.isSuperUser()
    },
  },
  async mounted() {
    await this.filterPages();
    await this.fetchData();


    this.coveryear_ids = this.coveryear_data.coveryear_ids.map(item => item.id_coveryear);
  },
  methods: {
    downloadFile(fileName) {
      this.$downloadFile({
        downloadFileName: fileName,
        url: COVERYEARS_ENDPOINTS.download_coveryear_CCR_threshold,
        query: {
          language: this.$store.state.language
        }
      })
    },
    handleCoverYearIdChange(coveryear_id){
      this.selected_coveryear_id = coveryear_id;
      fetchBranches(coveryear_id)
    },
    formatDate(date) {
      return moment(date).format('YYYY-MM-DD HH:mm:ss');
    },
    async handleUpload(files) {
      await Promise.all(Array.from(files).map((file) => this.uploadFile(file)));
    },
    async handleTaskResult(step) {
      this.uploadingFiles = false;
      this.uploadStep = step;
      await this.fetchData()
    },
    handleOnProgress(value) {
      this.uploadProgress = value;
    },
    async uploadFile(file) {
      this.uploadingFiles = true;
      this.uploadError = '';
      this.uploadProgress = 0;
      this.uploadStep = 'upload'
      this.uploadingFileName = file.name
      this.showUploadModal = true

      const query = {
        id_coveryear: this.coveryear_id
      }

      const response = await this.$uploadFile({
        fileId: 120,
        file: file,
        url: this.COVERYEARS_ENDPOINTS.inject_coveryear_CCR_threshold,
        query: query,
        onProgress: this.handleOnProgress,
        showAlerts: false
      })

      if (response && response.type == 'success') {
        this.uploadStep = 'inject';
        this.handleTaskResult('success')
        setTimeout(() => {
          window.location.reload();
        }, 3000);
      } else {
        this.uploadError = response.message
        this.handleTaskResult('error')
      }
    },
    async filterPages() {
      if (!this.hasAccessComplete) {
        this.pages = this.pages.filter(page => !page.only_admin)
      }
    },
    hasAccessAdmin() {
      return ActiveUser.hasRole(userRoles.Admin)
    },
    isSuperUser() {
      return ActiveUser.user.role === userRoles.SuperUser
    },
    handleRateInput(key_str, v) {
      if(parseFloat(v) === 0) v = '0';
      if (parseFloat(v).toFixed(2) > 100) v = '100'
      if (v !== '0') v = this.getNumericalValue(parseFloat(v), 2)

      this.rates = this.rates.map(rate => ({
        ...rate,
        value: key_str === rate.label ? v : rate.value
      }))
    },
    switchPage(page) {
      this.current_page = page;
    },
    isCurrentPage(page) {
      return this.current_page === page;
    },

    async fetchBranches(coveryear_id) {
      this.rates = await BranchesServices.getBranchList();

      const coveryearDataForYear = this.coveryear_data.coveryear_CCR_threshold[coveryear_id] || [];

      this.rates = this.rates.map((rate) => {
        const matchedCoveryearCCRThreshold = coveryearDataForYear.find(
          (coveryear_CCR_threshold) => coveryear_CCR_threshold.id_branch === rate.id_branch
        );

        return {
          label: this.$store.state.language == 'en' ? rate.EN : rate.branch,
          id_branch: rate.id_branch,
          value: matchedCoveryearCCRThreshold?.threshold || 0,
        };
      });

      if (!this.rates.length) {
        alert(this.$t("No Branch data. Please create Branch instance before creating new Cover Year"), {
              action: {
                  text: this.$t('Close'),
                  icon: 'close',
                  onClick: (e, toastObject) => {
                      toastObject.goAway(0);
                  }
              }
          })
      }
    },

    async fetchData() {
      const {data} = await this.$request({
        url: COVERYEARS_ENDPOINTS.coveryear_CCR_threshold
      })
      this.coveryear_data = data
    },
    async updateCoverYearCCRThreshold() {
      const negative_rates_flag = this.rates.some(item => parseFloat(item.value) < 0);
      if (negative_rates_flag) {
        alert(this.$t('Inputed rates must be positive'), {
                action: {
                    text: this.$t('Close'),
                    icon: 'close',
                    onClick: (e, toastObject) => {
                        toastObject.goAway(0);
                    }
                }
            });
            return
          }
      if (this.selected_coveryear_id!=null) {
        this.updating_coveryear = true
        const today = new Date();
        const {res} = await CoverYearServices.updateCoverYearCCRThreshold(this.selected_coveryear_id, {
        id_coveryear: parseInt(this.selected_coveryear_id),
        date_modification: moment(today).format('YYYY-MM-DD HH:mm:ss'),
        user_modification: ActiveUser?.user?.email,
        rates: this.rates,
      })
      if (res.status === 200) {
        alert(this.$t("Saved CoverYear Details"), {
              action: {
                  text: this.$t('Close'),
                  icon: 'close',
                  onClick: (e, toastObject) => {
                      toastObject.goAway(0);
                  }
              }
          })
        await this.fetchData();
      }
      this.updating_coveryear = false
      }
    },
  }
}

</script>

<style lang="scss">
.mz-button-custom-radius {
  border-radius: 10px !important;
}

.is-current-page {
  border-bottom: 2px solid #23145f;
}

.pages-nav {
  list-style: none;
  margin: 1rem 0;
  color: #23145f;

  li {
    font-size: 20px;
    font-weight: bold;
    display: inline;
    padding: 1rem;
    margin: 0 0.5rem;

    &:hover {
      cursor: pointer;
    }
  }
}

.arrow-buttons {
  display: none;
}

input[name="filter__ccr_group"] {
  display: none;
}

.modal-header {
  color: #fff;
  background-color: #23145f;
  border-color: #23145f;
}

.template-file {
  color: blue !important;
  text-decoration: underline !important;
  cursor: pointer;
}
</style>
